import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';

import { useClasses } from 'src/lib/styles';
import { cond, notEmpty } from 'src/utils';
import { LinkButton, Button } from '../atoms';
import Logo from '../../data/images/svgs/logo.svg';

const styles = {
  cursor: cond('', 'cursor-default'),
  current: cond('text-indigo-600'),
  hover: cond('visible', 'invisible'),
};

function MobileMenuLink({ to, current, subMenus, children }) {
  const menuClasses = useClasses(
    'px-5',
    'py-3',
    'lowercase',
    'hover:bg-indigo-100',
    'rounded-md',
    'text-lg',
    styles.current(current),
    styles.cursor(to),
  );

  const subMenuClasses = useClasses(
    'px-5',
    'py-3',
    'lowercase',
    'hover:bg-indigo-100',
    'rounded-md',
    'text-lg',
    styles.current(current),
    styles.cursor(to),
  );

  if (to) {
    return (
      <div className="my-8">
        <Link to={to} className={menuClasses}>
          {children}
        </Link>
        {notEmpty(subMenus) && (
          <div className="flex flex-col px-4 py-2">
            {subMenus.map((subMenu) => (
              <Link key={`${subMenu.name}-${subMenu.to}`} to={subMenu.to} className={subMenuClasses}>
                {subMenu.title}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center my-8">
      <div className={menuClasses}>{children}</div>
      {notEmpty(subMenus) && (
        <div className="flex flex-col items-center px-4 py-2 mt-1 bg-gray-100 rounded-md">
          {subMenus.map((subMenu) => (
            <Link key={`${subMenu.name}-${subMenu.to}`} to={subMenu.to} className={subMenuClasses}>
              {subMenu.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

MobileMenuLink.propTypes = {
  current: PropTypes.bool,
  to: PropTypes.string.isRequired,
  children: PropTypes.string.isRequired,
  subMenus: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
};

MobileMenuLink.defaultProps = {
  current: false,
  subMenus: [],
};

function Indicator({ active }) {
  const classes = useClasses('w-full', 'bg-indigo-300');

  return <div className={classes} style={{ height: 2, transition: 'max-width 300ms', maxWidth: active ? 100 : 0 }} />;
}

Indicator.propTypes = {
  active: PropTypes.bool.isRequired,
};

function MenuItem({ title, to, subMenus, current, className }) {
  const isCurrent = to === current || (notEmpty(subMenus) && subMenus.some((subMenu) => subMenu.to === current));
  const [hover, set] = useState(false);
  const onMouseEnter = () => set(true);
  const onMouseLeave = () => set(false);

  const subMenusClasses = useClasses(
    'absolute',
    'z-40',
    'border-t-8',
    'border-transparent',
    'min-w-full',
    styles.hover(hover),
  );

  if (to) {
    return (
      <div className={`relative ${className}`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <Link to={to} className="flex flex-col items-center px-6 py-4 text-center lowercase">
          <span>{title}</span>
          <div className="flex justify-center flex-1 w-full">
            <Indicator active={isCurrent || hover} />
          </div>
        </Link>
        {notEmpty(subMenus) && (
          <div className={subMenusClasses}>
            <div className="flex flex-col px-4 py-2 bg-white shadow-md rounded-md">
              {subMenus.map((subMenu) => (
                <MenuItem to={subMenu.to} title={subMenu.title} current={current} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="flex flex-col items-center px-6 py-4 text-center lowercase cursor-default">
        <div>{title}</div>
        <div className="flex justify-center flex-1 w-full">
          <Indicator active={isCurrent || hover} />
        </div>
      </div>
      {notEmpty(subMenus) && (
        <div className={subMenusClasses}>
          <div className="flex flex-col px-4 py-2 bg-white shadow-md rounded-md">
            {subMenus.map((subMenu) => (
              <MenuItem to={subMenu.to} title={subMenu.title} current={current} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

MenuItem.propTypes = {
  current: PropTypes.string,
  to: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subMenus: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ),
  className: PropTypes.string,
};

MenuItem.defaultProps = {
  current: null,
  subMenus: [],
  className: null,
};

function BrowserMenuLink({ to, current, subMenus, children }) {
  const [hover, set] = useState(false);
  const onMouseEnter = () => set(true);
  const onMouseLeave = () => set(false);

  const menuClasses = useClasses(
    'px-6',
    'py-4',
    'lowercase',
    'hover:bg-indigo-100',
    'rounded-md',
    styles.current(current),
    styles.cursor(to),
  );

  const subMenusClasses = useClasses(
    'absolute',
    'z-40',
    'border-t-8',
    'border-transparent',
    '-ml-4',
    styles.hover(hover),
    styles.current(current),
  );

  if (to) {
    return (
      <div className="mx-3" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <Link to={to} className={menuClasses}>
          {children}
        </Link>
        {notEmpty(subMenus) && (
          <div className={subMenusClasses}>
            <div className="flex flex-col px-4 py-2 bg-white shadow-md rounded-md">
              {subMenus.map((subMenu) => (
                <Link
                  key={`${subMenu.name}-${subMenu.to}`}
                  to={subMenu.to}
                  className="px-6 py-4 lowercase hover:bg-indigo-100 rounded-md"
                >
                  {subMenu.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-3" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className={menuClasses}>{children}</div>
      {notEmpty(subMenus) && (
        <div className={subMenusClasses}>
          <div className="flex flex-col px-4 py-2 bg-white shadow-md rounded-md">
            {subMenus.map((subMenu) => (
              <Link
                key={`${subMenu.name}-${subMenu.to}`}
                to={subMenu.to}
                className="px-6 py-4 lowercase hover:bg-indigo-100 rounded-md"
              >
                {subMenu.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

BrowserMenuLink.propTypes = {
  current: PropTypes.bool,
  to: PropTypes.string.isRequired,
  children: PropTypes.string.isRequired,
  subMenus: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
};

BrowserMenuLink.defaultProps = {
  current: false,
  subMenus: [],
};

const menuStyles = {
  visible: cond('visible', 'hidden'),
};

export function Menu({ lang, homeMenuTitle, menus, current, onOpen, onClose }) {
  const [isOpen, setIsOpen] = useState(false);
  const toogleMenu = () => {
    if (!isOpen && onOpen) onOpen();
    if (isOpen && onClose) onClose();
    setIsOpen(!isOpen);
  };

  const mobileMenuClasses = useClasses(
    'fixed',
    'top-0',
    'bottom-0',
    'left-0',
    'right-0',
    'z-40',
    'overflow-auto',
    'bg-white',
    menuStyles.visible(isOpen),
  );

  return (
    <nav className="flex items-center justify-between w-full mt-5">
      <Link to={`/${lang}`}>
        <div className="w-48 h-40 flex items-center">
          <Logo />
        </div>
      </Link>
      <div className="items-center justify-between hidden w-full lg:flex">
        <div className="flex items-center">
          <MenuItem key="home-menu" title={homeMenuTitle} to={`/${lang}`} current={current} />
          {menus.map((menu) => (
            <MenuItem
              key={`${menu.name}-${menu.to}`}
              title={menu.title}
              to={menu.to}
              subMenus={menu.subMenus}
              current={current}
            />
          ))}
        </div>
        <LinkButton to="/">{lang}</LinkButton>
      </div>
      <Button onClick={toogleMenu} className="z-50 visible lg:hidden">
        <div className="flex flex-col">
          <div className="w-6 h-1 bg-indigo-600" />
          <div className="w-6 h-1 mt-1 bg-indigo-600" />
          <div className="w-6 h-1 mt-1 bg-indigo-600" />
        </div>
      </Button>
      <div className={mobileMenuClasses}>
        <div className="flex flex-col items-center justify-center w-full py-20">
          <MobileMenuLink key="home-menu" to={`/${lang}`}>
            {homeMenuTitle}
          </MobileMenuLink>
          {menus.map((menu) => (
            <MobileMenuLink key={`${menu.name}-${menu.to}`} to={menu.to} subMenus={menu.subMenus}>
              {menu.title}
            </MobileMenuLink>
          ))}
          <LinkButton className="mt-3" to="/">
            {lang}
          </LinkButton>
        </div>
      </div>
    </nav>
  );
}

Menu.propTypes = {
  lang: PropTypes.string.isRequired,
  homeMenuTitle: PropTypes.string.isRequired,
  menus: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      to: PropTypes.string,
      subMenus: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          to: PropTypes.string.isRequired,
        }),
      ),
    }),
  ).isRequired,
  current: PropTypes.string.isRequired,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
};

Menu.defaultProps = {
  onOpen: null,
  onClose: null,
};
